import Cookies from 'js-cookie';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  deleteTokenInfo,
  KEY_MYGPT_PATH_NAME,
  KEY_MYGPT_REFRESH_TOKEN,
  KEY_MYGPT_TOKEN_EXPIRESIN,
  refreshToken as refreshTokenRequest,
  saveTokenInfo,
} from '@mygpt/app';
import { TokenInfo } from '@mygpt/types';

/**
 * 获取 Admin 登录页地址
 * 本地开发环境使用 localhost:5174（与 admin 开发服务器同域，cookie 共享）
 * 线上环境使用 VITE_ADMIN_URL（同域名下 cookie 共享）
 */
const getAdminLoginUrl = () => {
  if (['localhost', '127.0.0.1'].includes(window.location.hostname)) {
    return 'https://localhost:5174/auth/login';
  }
  const adminUrl = import.meta.env.VITE_ADMIN_URL || '';
  return adminUrl + '/auth/login';
};

/**
 * Website应用的登录hook
 * 登录跳转到 Admin 登录页，Cookie 在相同域名下共享
 */
export const useLogin = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const login = (redirectUrl?: string) => {
    const adminLoginUrl = getAdminLoginUrl();
    const params = redirectUrl ? `?redirect_uri=${btoa(encodeURI(redirectUrl))}` : '';
    window.location.href = `${adminLoginUrl}${params}`;
  };

  const logout = () => {
    deleteTokenInfo();

    const APIBaseURL = window.ENV?.GBASE_SUPPORT_FRONTEND_VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL;
    window.location.href = APIBaseURL + `/logout?redirect_uri=${encodeURI(getAdminLoginUrl())}`;
  };

  const loginCallback = () => {
    const pathname = sessionStorage.getItem(KEY_MYGPT_PATH_NAME);
    if (pathname && pathname !== location.pathname) {
      window.location.href = pathname;
    } else {
      navigate('/');
    }
    sessionStorage.removeItem(KEY_MYGPT_PATH_NAME);
  };

  const updateToken = (data: TokenInfo) => {
    saveTokenInfo(data);
    scheduleTokenRenewal();
  };

  const refreshToken = async () => {
    const refresh_token = Cookies.get(KEY_MYGPT_REFRESH_TOKEN());
    if (!refresh_token) return;
    const res = await refreshTokenRequest({ refresh_token });
    if (!res.data) return;
    const data: TokenInfo = res.data;
    updateToken(data);
    return data;
  };

  const scheduleTokenRenewal = () => {
    const expiresIn = Cookies.get(KEY_MYGPT_TOKEN_EXPIRESIN()) || 0;
    if (!expiresIn) return;

    const now = new Date().getTime();
    const delay = Number(expiresIn) - now - 60000;

    if (delay <= 0) return;

    setTimeout(() => {
      refreshToken();
    }, delay);
  };

  return {
    login,
    logout,
    loginCallback,
    updateToken,
    refreshToken,
    scheduleTokenRenewal,
  };
};
